import type { ContactListFilters } from "@/lib/queries";
import type { OpportunityListInput, OpportunityListRow } from "@/lib/types";

/**
 * Opportunities' smart lists, as GoHighLevel keeps them: a named tab over the board or a table, with
 * its own filters and sort. They are the signed-in person's own, saved whole in their `ui_prefs`
 * (`opportunity_lists`), as the reports' saved views are (ADR-074); nothing about them is on the server
 * beyond that blob, so a list is only ever a set of `GET /leads` parameters. The firm picker and the
 * search box sit over every list and are not part of one.
 *
 * "All opportunities" is built in: it is the board with nothing applied, and cannot be renamed or
 * deleted. Filters picked on it are kept for the visit, not saved.
 */

export const LISTS_PREF = "opportunity_lists";
export const ALL_LIST = "all";

export type ListView = "board" | "list";

/** What a filter row may test. Each is one `GET /leads` parameter (`contacts.Filters` in core-api). */
export const FILTER_FIELDS = [
  "stage", "status", "source", "language", "state", "dial_state", "dnc", "member", "created_from", "created_to",
] as const;
export type FilterField = (typeof FILTER_FIELDS)[number];

export interface ListFilter {
  field: FilterField;
  /** The picked values. Only `stage` takes several; the rest take one. */
  values: string[];
}

export const LIST_SORTS = ["created", "name", "stage", "phone"] as const;
export type ListSortField = (typeof LIST_SORTS)[number];

export interface ListSort {
  field: ListSortField;
  order: "asc" | "desc";
}

/** What a board card may show under the name: GoHighLevel's "Manage fields". */
export const CARD_FIELDS = ["source", "firm", "phone", "email", "language", "tags", "stage_since", "created"] as const;
export type CardField = (typeof CARD_FIELDS)[number];
export const DEFAULT_CARD_FIELDS: CardField[] = ["source", "firm", "tags"];

export const CARD_FIELD_TEXT: Record<CardField, string> = {
  source: "Source",
  firm: "Pipeline",
  phone: "Phone",
  email: "Email",
  language: "Language",
  tags: "Tags",
  stage_since: "In stage",
  created: "Created",
};

export interface SmartList {
  id: string;
  name: string;
  view: ListView;
  filters: ListFilter[];
  sort: ListSort;
  /** The card's fields, in `CARD_FIELDS` order. */
  fields: CardField[];
  /** Compact cards show the name, the dialer's chip and the icons only. */
  density: Density;
  /** On every board in the tenant, not only its owner's. */
  shared: boolean;
  /** The signed-in person made it. */
  mine: boolean;
  /** They may change or delete it: its owner, or an admin for a shared one. */
  canEdit: boolean;
  /** Who made it, for a shared list's tab. */
  ownerName: string | null;
}

export type Density = "comfortable" | "compact";

export const DEFAULT_SORT: ListSort = { field: "created", order: "desc" };

export const ALL_OPPORTUNITIES: SmartList = {
  id: ALL_LIST,
  name: "All opportunities",
  view: "board",
  filters: [],
  sort: DEFAULT_SORT,
  fields: DEFAULT_CARD_FIELDS,
  density: "comfortable",
  shared: false,
  mine: true,
  canEdit: false,
  ownerName: null,
};

export const FILTER_TEXT: Record<FilterField, { label: string; operator: string; kind: "stages" | "one" | "text" | "date" }> = {
  stage: { label: "Stage", operator: "Is any of", kind: "stages" },
  status: { label: "Status", operator: "Is", kind: "one" },
  source: { label: "Source", operator: "Starts with", kind: "text" },
  language: { label: "Language", operator: "Is", kind: "one" },
  state: { label: "Residence state", operator: "Is", kind: "text" },
  dial_state: { label: "Dialer state", operator: "Is", kind: "one" },
  dnc: { label: "Do not call", operator: "Is", kind: "one" },
  member: { label: "On a dial list", operator: "Is", kind: "one" },
  created_from: { label: "Created", operator: "On or after", kind: "date" },
  created_to: { label: "Created", operator: "Before", kind: "date" },
};

/** The fixed choices of the one-value fields. `status` is GoHighLevel's word for the stage's group. */
export const FILTER_CHOICES: Partial<Record<FilterField, { value: string; label: string }[]>> = {
  status: [
    { value: "working", label: "Open" },
    { value: "scheduled", label: "Scheduled" },
    { value: "exit", label: "Closed" },
  ],
  language: [
    { value: "en", label: "English" },
    { value: "es", label: "Spanish" },
  ],
  dnc: [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
  ],
  member: [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
  ],
};

export const SORT_TEXT: Record<ListSortField, string> = {
  created: "Created",
  name: "Opportunity name",
  stage: "Stage",
  phone: "Phone",
};

/** A filter row counts once it has a value; an empty one is a row still being filled in. */
export function isComplete(filter: ListFilter): boolean {
  return filter.values.some((v) => v.trim() !== "");
}

/** Why the filters cannot be applied yet, per row, or null: GoHighLevel's "Value cannot be empty". */
export function filterProblems(filters: readonly ListFilter[]): (string | null)[] {
  return filters.map((f) => (isComplete(f) ? null : "Value cannot be empty"));
}

/** How many filters a list applies, for "Advanced filters (n)". */
export function appliedCount(list: SmartList): number {
  return list.filters.filter(isComplete).length;
}

/** Whether the sort is the default, for "Sort (n)". */
export function sortApplied(sort: ListSort): boolean {
  return sort.field !== DEFAULT_SORT.field || sort.order !== DEFAULT_SORT.order;
}

/**
 * What `GET /leads` (and `/leads/board`) is asked for a list, over the firm picker and the search.
 * The board cannot order by stage — its columns are the stages — so it falls back to the newest.
 */
export function listApiFilters(
  list: SmartList,
  base: { firm: string; q: string },
  view: ListView = list.view,
): ContactListFilters {
  const out: ContactListFilters = {};
  if (base.firm !== "all") out.firm = base.firm;
  if (base.q.trim()) out.q = base.q.trim();
  for (const f of list.filters) {
    const values = f.values.map((v) => v.trim()).filter(Boolean);
    if (values.length === 0) continue;
    switch (f.field) {
      case "stage":
        out.stage = [...new Set([...(out.stage ? out.stage.split(",") : []), ...values])].join(",");
        break;
      case "status":
        out.stage_group = values[0];
        break;
      case "dnc":
      case "member":
        out[f.field] = values[0] === "true";
        break;
      case "state":
        out.state = values[0]!.toUpperCase();
        break;
      case "created_from":
      case "created_to":
        out[f.field] = new Date(`${values[0]}T00:00:00`).toISOString();
        break;
      default:
        out[f.field] = values[0];
    }
  }
  const sort = view === "board" && list.sort.field === "stage" ? DEFAULT_SORT : list.sort;
  out.sort = sort.field;
  out.order = sort.order;
  return out;
}

/**
 * The lists a person kept in `ui_prefs` before lists were rows, dropping anything that is not one.
 * The page moves them to `/opportunity-lists` once and clears the preference.
 */
export function readLists(prefs: Record<string, unknown> | undefined): SmartList[] {
  const raw = prefs?.[LISTS_PREF];
  if (!Array.isArray(raw)) return [];
  const out: SmartList[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const l = item as Record<string, unknown>;
    if (typeof l.id !== "string" || !l.id || l.id === ALL_LIST || typeof l.name !== "string") continue;
    const view: ListView = l.view === "list" ? "list" : "board";
    const filters = Array.isArray(l.filters)
      ? (l.filters as unknown[]).flatMap((f): ListFilter[] => {
          const r = f as Record<string, unknown> | null;
          if (!r || !(FILTER_FIELDS as readonly string[]).includes(r.field as string) || !Array.isArray(r.values)) return [];
          return [{ field: r.field as FilterField, values: (r.values as unknown[]).map(String) }];
        })
      : [];
    const s = (l.sort ?? {}) as Record<string, unknown>;
    const sort: ListSort = {
      field: (LIST_SORTS as readonly string[]).includes(s.field as string) ? (s.field as ListSortField) : DEFAULT_SORT.field,
      order: s.order === "asc" ? "asc" : "desc",
    };
    const fields = Array.isArray(l.fields)
      ? CARD_FIELDS.filter((f) => (l.fields as unknown[]).includes(f))
      : DEFAULT_CARD_FIELDS;
    const density: Density = l.density === "compact" ? "compact" : "comfortable";
    out.push({
      id: l.id, name: l.name.trim() || "Untitled list", view, filters, sort, fields, density,
      shared: false, mine: true, canEdit: true, ownerName: null,
    });
  }
  return out;
}

/** "New smart list 1", or the next number not taken. */
export function nextListName(lists: readonly SmartList[]): string {
  const taken = new Set(lists.map((l) => l.name));
  let n = 1;
  while (taken.has(`New smart list ${n}`)) n += 1;
  return `New smart list ${n}`;
}

/** A fresh list id: short, and not one already in use. */
export function newListId(lists: readonly SmartList[], random: () => number = Math.random): string {
  const taken = new Set([ALL_LIST, ...lists.map((l) => l.id)]);
  for (;;) {
    const id = `l${Math.floor(random() * 36 ** 6).toString(36).padStart(6, "0")}`;
    if (!taken.has(id)) return id;
  }
}

/** The lists with `list` put in its place, or added at the end. */
export function upsertList(lists: readonly SmartList[], list: SmartList): SmartList[] {
  const at = lists.findIndex((l) => l.id === list.id);
  if (at < 0) return [...lists, list];
  const next = [...lists];
  next[at] = list;
  return next;
}

/** The card fields with `field` switched on or off, kept in `CARD_FIELDS` order. */
export function toggleField(fields: readonly CardField[], field: CardField): CardField[] {
  const on = new Set(fields);
  if (on.has(field)) on.delete(field);
  else on.add(field);
  return CARD_FIELDS.filter((f) => on.has(f));
}

/** The tabs the bar shows and the ones "N More" folds away: the open one always shows. */
export function splitTabs<T extends { id: string }>(lists: readonly T[], active: string, room = 4): { shown: T[]; more: T[] } {
  if (lists.length <= room) return { shown: [...lists], more: [] };
  const shown = lists.slice(0, room);
  const open = lists.find((l) => l.id === active);
  if (open && !shown.includes(open)) shown[room - 1] = open;
  return { shown, more: lists.filter((l) => !shown.includes(l)) };
}

/**
 * The one-click chips over the board: each is one filter row. Chips on the same field replace each
 * other — `GET /leads` takes one dialer state and one language — and a chip pressed again comes off.
 */
export const QUICK_CHIPS = [
  { id: "fresh", label: "Not called yet", filter: { field: "dial_state", values: ["fresh"] } },
  { id: "retrying", label: "Retrying", filter: { field: "dial_state", values: ["retrying"] } },
  { id: "exhausted", label: "Exhausted", filter: { field: "dial_state", values: ["exhausted"] } },
  { id: "spanish", label: "Spanish", filter: { field: "language", values: ["es"] } },
  { id: "english", label: "English", filter: { field: "language", values: ["en"] } },
  { id: "off_list", label: "On no dial list", filter: { field: "member", values: ["false"] } },
  { id: "dnc", label: "Do not call", filter: { field: "dnc", values: ["true"] } },
] as const satisfies readonly { id: string; label: string; filter: ListFilter }[];
export type QuickChip = (typeof QUICK_CHIPS)[number];

/** Whether a chip's filter is among the list's. */
export function chipOn(filters: readonly ListFilter[], chip: QuickChip): boolean {
  return filters.some((f) => f.field === chip.filter.field && f.values.length === 1 && f.values[0] === chip.filter.values[0]);
}

/** The filters with a chip pressed: on replaces its field's rows, off removes the chip's own. */
export function toggleChip(filters: readonly ListFilter[], chip: QuickChip): ListFilter[] {
  const field = chip.filter.field as FilterField;
  const rest = filters.filter((f) => f.field !== field);
  if (chipOn(filters, chip)) return rest;
  return [...rest, { field, values: [...chip.filter.values] }];
}

/** A list as `/opportunity-lists` returns it, read the way the page keeps one. */
export function fromApi(row: OpportunityListRow): SmartList {
  const [parsed] = readLists({ [LISTS_PREF]: [{ ...row, id: row.id }] });
  const base = parsed ?? { ...ALL_OPPORTUNITIES, id: row.id, name: row.name };
  return { ...base, shared: row.shared, mine: row.mine, canEdit: row.can_edit, ownerName: row.owner_name };
}

/** What a create or a whole edit sends for `list`. */
export function toApi(list: SmartList): OpportunityListInput {
  return {
    name: list.name.trim(),
    view: list.view,
    filters: list.filters.filter(isComplete).map((f) => ({ field: f.field, values: f.values.map((v) => v.trim()).filter(Boolean) })),
    sort: list.sort,
    fields: [...list.fields],
    density: list.density,
    shared: list.shared,
  };
}
